import { createContext, useCallback, useContext, useRef, useEffect } from "react";
import { createSignalRConnection } from "../utils/signalr-connection";
import { useAuth } from "./AuthContext";
import { useDialog } from './DialogContext';
import { WithdrawalContext } from './WithdrawalContext';
import { decodeJwtPayload } from '../utils/jwtUtils';

const SignalRContext = createContext(null);

export const useSignalR = () => useContext(SignalRContext);

export const SignalRProvider = ({ children }) => {
    const connectionRef    = useRef(null);
    const intentionalStop  = useRef(false);  // prevents onclose from fighting disconnect()
    const { isAuthenticated, user, loading, logout } = useAuth();
    const { getProcessingWithdrawalId } = useContext(WithdrawalContext);
    const { openDialog, closeDialog } = useDialog();

    const startConnection = useCallback(async (connection) => {
        try {
            await connection.start();
            console.log("✅ SignalR connected");
        } catch (err) {
            console.error("❌ Initial connect failed", err);
            setTimeout(() => startConnection(connection), 5000);
        }
    }, []);

    const disconnect = useCallback(async () => {
        if (connectionRef.current) {
            intentionalStop.current = true;
            await connectionRef.current.stop();
            connectionRef.current = null;
            intentionalStop.current = false;
            console.log("🔌 SignalR disconnected");
        }
    }, []);

    const connect = useCallback(
        async (userName) => {
        if (connectionRef.current) return;

        const connection = createSignalRConnection(userName);
        connectionRef.current = connection;

        // 🔄 Auto reconnect — skipped when disconnect() was called intentionally
        connection.onclose(async () => {
            if (intentionalStop.current) return;
            console.warn("⚠️ Connection closed. Trying to reconnect...");
            await startConnection(connection);
        });

        // 📩 Listen to messages
        connection.on("ReceiveMessage", async (message) => {
            console.log("📩 SignalR received:", message);

            let parsed;
            try {
                parsed = JSON.parse(message);
            } catch {
                console.error("Failed to parse SignalR message:", message);
                return;
            }

            const { ActionTypeId: actionTypeId, TargetJti: targetJti } = parsed;

            if (actionTypeId === 1) {
                // New withdrawal reassigned — show reminder dialog if already processing one
                const withdrawalId = await getProcessingWithdrawalId();
                if (withdrawalId) {
                    openDialog({
                        title: "新申购提醒",
                        message: "您有新的申购请求，请立即前往处理。",
                        confirmText: "好的",
                        onConfirm: () => {
                            closeDialog();
                            window.location.href = "/payout";
                        },
                        showCancelButton: false,
                    });
                }
            } else if (actionTypeId === 2) {
                // Force logout — another device has taken over this account.
                // Check TargetJti so the NEW session ignores a kick meant for the OLD session.
                if (targetJti) {
                    const myToken = localStorage.getItem("accessToken");
                    const myJti = myToken ? decodeJwtPayload(myToken)?.jti : null;
                    if (myJti && myJti !== targetJti) {
                        console.log("📨 Kick received but targets a different session — ignoring");
                        return;
                    }
                }
                console.warn("🚫 Force logout received via SignalR — another device logged in");
                logout(true /* isKicked */);
            }
        });

        // 🚀 Start (with retry)
        await startConnection(connection);
        },
        [getProcessingWithdrawalId, openDialog, closeDialog, startConnection, logout]
    );

    // Connect on login / reconnect on page reload.
    // Disconnect on logout, session timeout, or force-kick.
    useEffect(() => {
        if (!loading && isAuthenticated && user?.username) {
            connect(user.username);
        } else if (!loading && !isAuthenticated) {
            disconnect();
        }
    }, [loading, isAuthenticated, user, connect, disconnect]);

    return (
        <SignalRContext.Provider value={{ connect, disconnect, connection: connectionRef.current }}>
        {children}
        </SignalRContext.Provider>
    );
};
